import { Text, View } from "@react-pdf/renderer";
import amlReportStyles from "../Styles/amlReportStyles";
import theme from "../../../../../css/WhiteLabelledDocuments/theme";

import capitalizeFirst from "../../Checks/utils/factories/capitalise";
import prettifyEnumName from "../../CheckDetails/Utils/prettifyEnumName";

import { getDecisionForService } from "../../CheckDetails/Utils/Experian/getServiceDecision";
import experianServiceNames from "../../CheckDetails/Utils/Experian/getExperianServiceNames";
import { alertsConfig } from "../../CheckDetails/Utils/Experian/getItemsAlerts";
import { dataSourceAlertMap } from "../../CheckDetails/Utils/Experian/flagDataSourceAlert";
import { getMortalityDecision } from "../../CheckDetails/Utils/Experian/getMortalityDetails";
import getIconAndColorAndDecision from "../Utils/getIconColorDecision";

const normTxt = (v) => (v ?? "").toString().trim().toLowerCase();
const cleanEnds = (s) => normTxt(s).replace(/[)\].,:;_-]+$/g, "");
const labelIs = (row, txt) => normTxt(row?.label) === normTxt(txt);

// ---------- CAPS / CAIS helpers ----------
const endsWithCaps = (row) => {
  const k = cleanEnds(row?.key);
  const l = cleanEnds(row?.label);
  return k.endsWith("caps") || l.endsWith("caps");
};

const endsWithCais = (row) => {
  const k = cleanEnds(row?.key);
  const l = cleanEnds(row?.label);
  return k.endsWith("cais") || l.endsWith("cais");
};

const groupCapsIntoSecondarySources = (rows = []) => {
  const capsRows = rows.filter(endsWithCaps);
  const nonCapsRows = rows.filter((r) => !endsWithCaps(r));

  if (!capsRows.length) return nonCapsRows;

  return [
    ...nonCapsRows,
    {
      key: "caps_group",
      label: "Secondary Sources",
      checks: capsRows.reduce((s, r) => s + (r.checks || 0), 0),
      sources: capsRows.reduce((s, r) => s + (r.sources || 0), 0),
      _isCapsGroup: true,
    },
  ];
};

const tagForPdf = (row) => {
  if (row?._isCapsGroup) return "";
  if (endsWithCais(row)) return " (CAIS)";
  return "";
};

// ---------- template + rows ----------
const pickTemplateElement = (payload, serviceName) => {
  const aliases = Array.isArray(serviceName) ? serviceName : [serviceName];
  return payload?.decisionElements?.find((d) =>
    aliases.some((s) => normTxt(d?.serviceName) === normTxt(s))
  );
};

const sumCounts = (counts, predicate) =>
  (counts || [])
    .filter(predicate)
    .reduce((sum, dc) => sum + (dc?.value || 0), 0);

const buildDataSourceRows = (response, serviceName) => {
  const payload = response?.clientResponsePayload;
  if (!payload) {
    return {
      visibleRows: [],
      totalChecks: 0,
      totalSources: 0,
      allDataCounts: [],
    };
  }

  const templateElement = pickTemplateElement(payload, serviceName);
  if (!templateElement) {
    return {
      visibleRows: [],
      totalChecks: 0,
      totalSources: 0,
      allDataCounts: [],
    };
  }

  const template =
    templateElement?.otherData?.response?.dataLevelTemplate || {};
  const enums = [...(template.primary || []), ...(template.secondary || [])];
  const allDataCounts = templateElement?.dataCounts || [];

  const rows = enums.map((enumName) => {
    const enumLower = normTxt(enumName);

    const checks = sumCounts(allDataCounts, (dc) => {
      const name = normTxt(dc?.name);
      return name.startsWith("itemscurrentaddress") && name.includes(enumLower);
    });

    const sources = sumCounts(allDataCounts, (dc) => {
      const name = normTxt(dc?.name);
      const isDsType = name.startsWith("datasourcetypescurrentaddress");
      const isDs = name.startsWith("datasourcescurrentaddress");
      return (isDsType || isDs) && name.includes(enumLower);
    });

    return {
      key: enumName,
      label: prettifyEnumName(enumName),
      checks,
      sources,
    };
  });

  // PDF keeps only rows with values
  const filteredRows = rows.filter(
    (r) => (r.checks || 0) > 0 || (r.sources || 0) > 0
  );

  const visibleRows = groupCapsIntoSecondarySources(filteredRows);

  const totalChecks = visibleRows.reduce((s, r) => s + (r.checks || 0), 0);
  const totalSources = visibleRows.reduce((s, r) => s + (r.sources || 0), 0);

  return { visibleRows, totalChecks, totalSources, allDataCounts };
};

// ---------- alerts ----------
const buildAlertRows = (response, allDataCounts) => {
  const payload = response?.clientResponsePayload;
  if (!payload) return [];

  const allDataCountsLocal =
    allDataCounts ||
    (payload.decisionElements || []).flatMap((d) => d.dataCounts || []);

  const mort = getMortalityDecision(
    response,
    experianServiceNames.CREDIT_REFERENCE
  );

  return alertsConfig.map((cfg) => {
    if (normTxt(cfg.key) === "mortality") {
      return {
        ...cfg,
        value: mort?.status ? 1 : 0,
        mortalityDecision: mort || null,
      };
    }

    let value = 0;

    if (cfg.type === "dataCount" && cfg.fieldName) {
      value = sumCounts(
        allDataCountsLocal,
        (dc) => normTxt(dc?.name) === normTxt(cfg.fieldName)
      );
    }

    if (cfg.type === "orchestratorDecision") {
      const decision = normTxt(
        getDecisionForService(response, experianServiceNames.ID_VERIFICATION)
      );
      value = decision === "continue" ? 1 : 0;
    }

    return { ...cfg, value, status: value === 0 ? "fail" : "pass" };
  });
};

// ---------- PDF highlight styles ----------
const ALERT_BG = theme.colors.Yellow400 || "#FFF6D1";
const ALERT_BORDER =
  theme.colors.Warning || theme.colors.NaplesYellow || "#F1DB4B";

const DatabaseVerification = ({ check, response, serviceName = "uk-idv" }) => {
  const { visibleRows, totalChecks, totalSources, allDataCounts } =
    buildDataSourceRows(response, serviceName);

  const rawDecision = getDecisionForService(
    response,
    experianServiceNames.ID_VERIFICATION
  );

  const uiDecisionLower = rawDecision ? normTxt(rawDecision) : null;
  const uiDecision = uiDecisionLower
    ? capitalizeFirst(uiDecisionLower)
    : "Not Started";

  const meta = getIconAndColorAndDecision((uiDecision || "").toLowerCase());
  if (!meta) return null;

  const { icon, color, decision } = {
    icon: meta.icon,
    color: meta.color,
    decision: uiDecision,
  };

  // ---- detect Mortgage / Rental Leasing presence (from visible data sources) ----
  const hasMortgage = visibleRows.some((r) => labelIs(r, "Mortgage"));
  const hasRentalLeasing = visibleRows.some((r) =>
    labelIs(r, "Rental Leasing")
  );
  const bothMR = hasMortgage && hasRentalLeasing;

  // ---- detect Loan Short presence (from alertsConfig + datacounts) ----
  const hasLoanShort = alertsConfig.some((cfg) => {
    if (normTxt(cfg.key) !== "loan short term small value") return false;
    const count = sumCounts(allDataCounts, (dc) =>
      normTxt(dc?.name).startsWith("itemsshorttermmoneysmallvalue")
    );
    return count > 0;
  });

  // ---- apply YOUR UI rules to decide which DS rows get special alert highlight ----
  const shouldFlagMortgage = bothMR || (hasLoanShort && hasMortgage); // loan short + mortgage only OR both mortgage+rental
  const shouldFlagRental = bothMR || (hasLoanShort && hasRentalLeasing); // loan short + rental only OR both mortgage+rental

  // ---- mortality alert (kept as-is) ----
  const alertRows = buildAlertRows(response, allDataCounts);
  const mortalityAlert = alertRows.find((r) => normTxt(r.key) === "mortality");
  const mort = mortalityAlert?.mortalityDecision || null;

  const mortalityLabel =
    mort?.status === "pass" || mort?.status === "pass_overridden"
      ? "Pass"
      : mort?.status === "needs_user_confirmation"
      ? "Fail"
      : "Unknown";

  const mortalityColor =
    mort?.status === "pass" || mort?.status === "pass_overridden"
      ? theme.colors.Success
      : theme.colors.Error;

  return (
    <View style={amlReportStyles.section} break={!!check?.verification}>
      <View style={amlReportStyles.headingRow}>
        <Text style={amlReportStyles.amlTextHeading}>
          Database Verification
        </Text>

        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          {icon}
          <Text style={{ ...amlReportStyles.amlDecisionTextSubheading, color }}>
            {decision}
          </Text>
        </View>
      </View>
      {/* Data Sources */}
      <View
        style={{
          ...amlReportStyles.table,
          border: `1px solid ${theme.colors.Grey400}`,
        }}
      >
        <View
          style={[amlReportStyles.tableRow, amlReportStyles.tableHeader]}
          wrap={false}
        >
          <Text
            style={{
              ...amlReportStyles.tableCell,
              ...amlReportStyles.amlTextSubheading,
              flex: 4,
            }}
          >
            Data Sources ({visibleRows.length})
          </Text>

          <Text
            style={{
              ...amlReportStyles.tableCell,
              ...amlReportStyles.amlTextSubheading,
              flex: 1,
              textAlign: "center",
            }}
          >
            Checks ({totalChecks})
          </Text>

          <Text
            style={{
              ...amlReportStyles.tableCell,
              ...amlReportStyles.amlTextSubheading,
              flex: 1,
              textAlign: "center",
            }}
          >
            Sources ({totalSources})
          </Text>
        </View>

        {visibleRows.map((row, idx) => {
          const isMortgageRow = labelIs(row, "Mortgage");
          const isRentalRow = labelIs(row, "Rental Leasing");
          const isRentalOrMortgage = isMortgageRow || isRentalRow;

          // existing map-driven alerts (same as your UI pattern)
          const baseMeta = !isRentalOrMortgage
            ? dataSourceAlertMap[row.label]
            : undefined;
          const baseAlert = !!baseMeta;

          // special rules you asked for
          const specialAlert =
            (isMortgageRow && shouldFlagMortgage) ||
            (isRentalRow && shouldFlagRental);

          const hasAlert = baseAlert || specialAlert;

          const zebraBg =
            idx % 2 !== 0 ? theme.colors.GhostWhite : theme.colors.White;

          return (
            <View
              key={row.key}
              style={{
                ...amlReportStyles.tableRow,
                backgroundColor: hasAlert ? ALERT_BG : zebraBg,
                // borderLeftWidth: hasAlert ? 3 : 0,
                // borderLeftColor: hasAlert ? ALERT_BORDER : "transparent",
                paddingLeft: hasAlert ? 6 : 8,
              }}
              wrap={false}
            >
              <Text
                style={{
                  ...amlReportStyles.tableCell,
                  ...amlReportStyles.amlTextCaption,
                  flex: 4,
                }}
              >
                {row.label}
                {tagForPdf(row)}
                {row._isCapsGroup ? " (Grouped CAPS)" : ""}
                {hasAlert ? " (Alert)" : ""}
              </Text>

              <Text
                style={{
                  ...amlReportStyles.tableCell,
                  ...amlReportStyles.amlTextCaption,
                  flex: 1,
                  textAlign: "center",
                }}
              >
                {row.checks}
              </Text>

              <Text
                style={{
                  ...amlReportStyles.tableCell,
                  ...amlReportStyles.amlTextCaption,
                  textAlign: "center",
                  flex: 1,
                }}
              >
                {row.sources}
              </Text>
            </View>
          );
        })}
      </View>
      {/* Alerts (Mortality only kept here — unchanged) */}
      {mortalityAlert && mort && (
        <View
          style={{
            ...amlReportStyles.table,
            border: `1px solid ${theme.colors.Grey400}`,
            marginTop: 10,
          }}
        >
          <View
            style={[amlReportStyles.tableRow, amlReportStyles.tableHeader]}
            wrap={false}
          >
            <Text
              style={{
                ...amlReportStyles.tableCell,
                ...amlReportStyles.amlTextSubheading,
                flex: 4,
              }}
            >
              Alerts
            </Text>
            <Text
              style={{
                ...amlReportStyles.tableCell,
                ...amlReportStyles.amlTextSubheading,
                flex: 2,
              }}
            >
              Status
            </Text>
          </View>

          <View style={{ ...amlReportStyles.tableRow }} wrap={false}>
            <Text
              style={{
                ...amlReportStyles.tableCell,
                ...amlReportStyles.amlTextCaption,
                flex: 4,
              }}
            >
              {mortalityAlert.label}
              {mort?.status === "pass_overridden" ? " (User confirmed)" : ""}
            </Text>

            <Text
              style={{
                ...amlReportStyles.tableCell,
                ...amlReportStyles.amlTextCaption,
                flex: 2,
                color: mortalityColor,
              }}
            >
              {mortalityLabel}
            </Text>
          </View>
        </View>
      )}
      {mortalityLabel === "Fail" ? (
        <View style={{ ...amlReportStyles.warningContainer, marginTop: 4 }}>
          <Text
            style={{
              ...amlReportStyles.amlTextCaption,
            }}
          >
            Refer check to a member of the compliance team.
          </Text>
        </View>
      ) : null}
      <View style={amlReportStyles.verticalSizeBox} />
    </View>
  );
};

export default DatabaseVerification;
